/**
 * Íconos del panel de Rastreo. SVG en línea, con trazo y sin relleno de marca:
 * heredan el color del texto (`currentColor`), así el estado lo sigue diciendo el
 * texto que los acompaña y no el ícono solo.
 *
 * Todos son decorativos (`aria-hidden`): quien los usa pone al lado su palabra o
 * un `sr-only`.
 */

import type { ReactNode } from "react";

function Svg({ className = "size-5", children }: { className?: string; children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

export const CarIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M5 16V11.5L6.8 7.2A2 2 0 0 1 8.6 6h6.8a2 2 0 0 1 1.8 1.2L19 11.5V16" />
    <path d="M3.5 11.5h17" />
    <rect x="4" y="11.5" width="16" height="5" rx="1.2" />
    <path d="M6.5 16.5V18M17.5 16.5V18" />
    <circle cx="7.8" cy="14" r="0.6" fill="currentColor" />
    <circle cx="16.2" cy="14" r="0.6" fill="currentColor" />
  </Svg>
);

export const SearchIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Svg>
);

export const FilterIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M4 6h16M7 12h10M10 18h4" />
  </Svg>
);

export const PlusIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const MapIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="m9 4-5 2v14l5-2 6 2 5-2V4l-5 2-6-2Z" />
    <path d="M9 4v14M15 6v14" />
  </Svg>
);

export const ReportIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5M10 13h6M10 17h6" />
  </Svg>
);

export const LogoutIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M14 4h5v16h-5" />
    <path d="M10 8l-4 4 4 4M6 12h10" />
  </Svg>
);

export const ListIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <circle cx="4.5" cy="6" r="1" fill="currentColor" />
    <circle cx="4.5" cy="12" r="1" fill="currentColor" />
    <circle cx="4.5" cy="18" r="1" fill="currentColor" />
  </Svg>
);

export const ChevronIcon = ({ className, open }: { className?: string; open: boolean }) => (
  <Svg className={`${className ?? "size-4"} transition-transform ${open ? "rotate-90" : ""}`}>
    <path d="m9 6 6 6-6 6" />
  </Svg>
);

export const CloseIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);

export const EditIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <path d="M4 20h4L19 9l-4-4L4 16z" />
    <path d="m13.5 6.5 4 4" />
  </Svg>
);

export const ColumnsIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M9.5 4v16M14.5 4v16" />
  </Svg>
);

/** Llave de encendido: ignición. */
export const IgnitionIcon = ({ className }: { className?: string }) => (
  <Svg className={className}>
    <circle cx="8" cy="12" r="3.5" />
    <path d="M11.5 12H20M17 12v3M20 12v2.5" />
  </Svg>
);

/** Batería con el nivel dibujado. `level` de 0 a 100, o `null` si el equipo no lo mandó. */
export const BatteryIcon = ({ className, level }: { className?: string; level: number | null }) => {
  const width = level === null ? 0 : Math.max(1.5, (Math.min(100, Math.max(0, level)) / 100) * 11);
  return (
    <Svg className={className}>
      <rect x="3" y="7.5" width="15" height="9" rx="1.8" />
      <path d="M20.5 10.5v3" />
      {level === null ? (
        <path d="M8 12h5" strokeDasharray="1.5 1.5" />
      ) : (
        <rect x="5" y="9.5" width={width} height="5" rx="0.6" fill="currentColor" stroke="none" />
      )}
    </Svg>
  );
};
